// src/screens/sales/category/hooks/useCategory.ts

import { useCallback, useEffect, useState } from 'react'
import { CategoryAttribute, CategoryInterface } from '../../../../common/types/interfaces'
import { getSalesApi } from '../../../../salesApi/salesApi'
import { selectCurrentCluster } from '../../../../store/slices/currentClusterSlice'
import { useSelector } from 'react-redux'
import { v4 } from 'uuid'

const initialValue: CategoryInterface = {
  name: '',
  description: '',
  attributes: [],
  files: [],
  sortOrder: '',
  fields: [],
  isActive: true,
  children: [],
}

/**
 * Нормализует children из CategoryInterface[] (с бека) в number[] (для формы)
 */
const normalizeChildren = (children: any): number[] => {
  if (!children || !Array.isArray(children)) return []
  return children
    .map((c: any) => (typeof c === 'object' ? Number(c.id) : Number(c)))
    .filter((id) => !isNaN(id))
}

export const useCategory = () => {
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [salesApi] = useState(getSalesApi())
  const [data, setData] = useState<CategoryInterface>(initialValue)
  const [list, setList] = useState<CategoryInterface[]>([])
  const [detailsModalActive, setDetailsModalActive] = useState<boolean>(false)
  const [current, setCurrent] = useState<CategoryInterface>()
  const currentCluster = useSelector(selectCurrentCluster)
  const [loading, setLoading] = useState(true)

  const getList = useCallback(async () => {
    try {
      const result = await salesApi.getCategoryList({ sort: 'name', order: 'ASC' })
      if (result) {
        const sortedData = [...result.data].sort((a, b) => {
          const orderA = a.sortOrder ? Number(a.sortOrder) : 0
          const orderB = b.sortOrder ? Number(b.sortOrder) : 0
          if (orderA !== orderB) return orderA - orderB
          return a.name.localeCompare(b.name)
        })
        setList(sortedData)
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }, [salesApi])

  const startCreate = (qr?: string) => {
    setCurrent(undefined)
    setEdit(false)
    setData({ ...initialValue })
    setModalActive(true)
  }

  const startEdit = (el: CategoryInterface) => {
    // Нормализуем children в number[]
    const normalized: CategoryInterface = {
      ...el,
      children: normalizeChildren(el.children),
    }
    setCurrent(normalized)
    setEdit(true)
    setData(normalized)
    setModalActive(true)
  }

  const startDuplicate = useCallback((sourceItem: CategoryInterface) => {
    const { id, createdAt, updatedAt, iiko_id, c1_id, moysklad_id, ...rest } = sourceItem

    // Очищаем id у каждого атрибута
    const cleanedAttributes =
      sourceItem.attributes?.map((attr: CategoryAttribute) => {
        const { id: attrId, ...attrWithoutId } = attr
        return {
          ...attrWithoutId,
          id: v4(),
        }
      }) || []

    const duplicatedCategory: CategoryInterface = {
      ...rest,
      name: `${sourceItem.name} (копия)`,
      files: sourceItem.files || [],
      attributes: cleanedAttributes,
      sortOrder: '',
      description: sourceItem.description || '',
      children: [], // ← очищаем детей при дублировании
    }

    setCurrent(duplicatedCategory)
    setEdit(false)
    setData(duplicatedCategory)
    setModalActive(true)
  }, [])

  const showDetails = useCallback((el: CategoryInterface) => {
    setCurrent(el)
    setDetailsModalActive(true)
  }, [])

  useEffect(() => {
    if (currentCluster) getList()
  }, [currentCluster, getList])

  useEffect(() => {
    if (!modalActive) setData(initialValue)
  }, [modalActive])

  return {
    getList,
    modalActive,
    setModalActive,
    startCreate,
    startEdit,
    edit,
    data,
    setData,
    list,
    detailsModalActive,
    setDetailsModalActive,
    showDetails,
    current,
    loading,
    startDuplicate,
  }
}
